import { useMemo } from 'react';
import type { InvestmentAccount, InvestmentTransaction } from '@/types/investments';

export interface InvestmentMetrics {
  totalDeposits: number;
  totalWithdrawals: number;
  netContribution: number;
  returnOnInvestment: number | null;
  totalInterest: number;
  currentMonthInterest: number;
  currentYearInterest: number;
}

/**
 * Hook to calculate investment performance metrics from account and transaction data
 * All calculations are performed client-side
 * 
 * @param account - The investment account data
 * @param transactions - Array of transactions for the investment account
 * @returns Object containing calculated metrics and loading state
 * 
 * @example
 * const { metrics, isLoading } = useInvestmentMetrics(account, transactions);
 */
export function useInvestmentMetrics(
  account: InvestmentAccount | undefined,
  transactions: InvestmentTransaction[] | undefined
): { metrics: InvestmentMetrics; isLoading: boolean } {
  const metrics = useMemo(() => {
    if (!account || !transactions) {
      return {
        totalDeposits: 0,
        totalWithdrawals: 0,
        netContribution: 0,
        returnOnInvestment: null,
        totalInterest: 0,
        currentMonthInterest: 0,
        currentYearInterest: 0,
      };
    }

    // Calculate total deposits (sum of all deposit transactions)
    const totalDeposits = transactions
      .filter(t => t.transaction_subtype === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);

    // Calculate total withdrawals (sum of all withdrawal transactions including fees)
    const totalWithdrawals = transactions
      .filter(t => t.transaction_subtype === 'withdrawal')
      .reduce((sum, t) => sum + t.amount + t.fee, 0);

    // Calculate net contribution (deposits - withdrawals)
    const netContribution = totalDeposits - totalWithdrawals;

    // Calculate return on investment percentage
    // ROI = ((currentBalance - netContribution) / netContribution) * 100
    // Returns null when netContribution is 0 to avoid division by zero
    const returnOnInvestment = netContribution !== 0
      ? ((account.balance - netContribution) / netContribution) * 100
      : null;

    // Calculate total interest earned
    // Total Interest = currentBalance - startingAmount - netContribution
    const totalInterest = account.balance - account.starting_amount - netContribution;

    // Get current date information for filtering
    const now = new Date();
    const currentMonth = now.getMonth();
    const currentYear = now.getFullYear();

    // Calculate current month interest
    // Filter transactions to current month and calculate interest from those
    const currentMonthTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.transaction_date);
      return transactionDate.getMonth() === currentMonth && 
             transactionDate.getFullYear() === currentYear;
    });

    const currentMonthDeposits = currentMonthTransactions
      .filter(t => t.transaction_subtype === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);

    const currentMonthWithdrawals = currentMonthTransactions
      .filter(t => t.transaction_subtype === 'withdrawal')
      .reduce((sum, t) => sum + t.amount + t.fee, 0);

    // Calculate interest for current month based on balance changes
    // This is a simplified calculation - actual interest would depend on the interest rate
    // For now, we calculate the net change in balance from transactions this month
    const currentMonthInterest = account.interest_rate > 0
      ? (currentMonthDeposits - currentMonthWithdrawals) * (account.interest_rate / 100 / 12)
      : 0;

    // Calculate current year interest
    const currentYearTransactions = transactions.filter(t => {
      const transactionDate = new Date(t.transaction_date);
      return transactionDate.getFullYear() === currentYear;
    });

    const currentYearDeposits = currentYearTransactions
      .filter(t => t.transaction_subtype === 'deposit')
      .reduce((sum, t) => sum + t.amount, 0);

    const currentYearWithdrawals = currentYearTransactions
      .filter(t => t.transaction_subtype === 'withdrawal')
      .reduce((sum, t) => sum + t.amount + t.fee, 0);

    // Calculate interest for current year
    const currentYearInterest = account.interest_rate > 0
      ? (currentYearDeposits - currentYearWithdrawals) * (account.interest_rate / 100)
      : 0;

    return {
      totalDeposits,
      totalWithdrawals,
      netContribution,
      returnOnInvestment,
      totalInterest,
      currentMonthInterest,
      currentYearInterest,
    };
  }, [account, transactions]);

  const isLoading = !account || !transactions;

  return { metrics, isLoading };
}
